import { useEffect, useState } from 'react';
import {
  createLinkedForm,
  findLinkedForm,
  publicFormUrl,
  sheetHeaders,
  type SheetLink,
} from '@/lib/formSheet';
import type { DocDocument } from '@/lib/documents';

interface FormLinkModalProps {
  userId: number;
  document: DocDocument;
  onClose: () => void;
}

/**
 * Relie un classeur Excel à un formulaire public : chaque réponse remplie par
 * un visiteur est ensuite ajoutée automatiquement dans une nouvelle ligne.
 */
export default function FormLinkModal({ userId, document, onClose }: FormLinkModalProps) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState<SheetLink | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const headers = sheetHeaders(document.content);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const found = await findLinkedForm(userId, document.id);
        if (active) setLink(found);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [userId, document.id]);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await createLinkedForm(userId, document);
      setLink(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Création impossible.');
    } finally {
      setBusy(false);
    }
  };

  const url = link ? publicFormUrl(link.formId) : '';

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Impossible de copier le lien automatiquement.");
    }
  };

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-foreground-950 flex items-center gap-2">
              <i className="ri-survey-line text-primary-500"></i>
              Formulaire lié au classeur
            </h3>
            <p className="mt-0.5 truncate text-xs text-foreground-500">{document.name}</p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 flex-shrink-0 cursor-pointer items-center justify-center rounded-full text-foreground-500 hover:bg-background-100"
            title="Fermer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
          </div>
        ) : (
          <>
            <div className="mb-4 rounded-xl border border-background-200/70 bg-background-100/60 p-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-foreground-500">
                Colonnes du classeur
              </p>
              {headers.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {headers.map((h) => (
                    <span
                      key={h}
                      className="rounded-full bg-white px-2.5 py-1 text-[11px] text-foreground-700 border border-background-200/70"
                    >
                      {h}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-foreground-500">
                  Aucune colonne détectée : la première ligne du classeur deviendra l&apos;en-tête
                  (Nom, Email, Téléphone…).
                </p>
              )}
            </div>

            {link ? (
              <div className="space-y-3">
                <div className="flex items-start gap-2 rounded-lg bg-emerald-50 px-3 py-2.5 text-xs text-emerald-800">
                  <i className="ri-check-double-line mt-0.5"></i>
                  <span>
                    Formulaire actif. Chaque réponse arrive automatiquement dans la prochaine ligne
                    libre du classeur, avec la date.
                  </span>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-foreground-600">
                    Lien public du formulaire
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={url}
                      className="min-w-0 flex-1 rounded-lg border border-background-200/70 bg-white px-3 py-2 font-mono text-[11px] text-foreground-700"
                    />
                    <button
                      onClick={copy}
                      className="flex flex-shrink-0 cursor-pointer items-center gap-1 rounded-full bg-secondary-100 px-3 py-2 text-[11px] font-semibold text-secondary-800 hover:bg-secondary-200/70 whitespace-nowrap"
                    >
                      <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                      {copied ? 'Copié' : 'Copier'}
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-full bg-primary-500 px-3 py-2.5 text-xs font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap no-underline"
                  >
                    <i className="ri-external-link-line"></i>
                    Ouvrir le formulaire
                  </a>
                  <a
                    href={`/dashboard/apps/forms/${link.formId}/responses`}
                    className="flex cursor-pointer items-center justify-center gap-1.5 rounded-full border border-background-200/70 bg-white px-3 py-2.5 text-xs font-semibold text-foreground-700 hover:bg-background-100 whitespace-nowrap no-underline"
                  >
                    <i className="ri-file-list-3-line"></i>
                    Réponses
                  </a>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs leading-relaxed text-foreground-600">
                  Créez un formulaire de saisie à partir des colonnes de ce classeur. Les réponses
                  des visiteurs seront écrites directement dans les lignes — sans copier-coller.
                </p>
                <button
                  onClick={create}
                  disabled={busy}
                  className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-primary-500 px-4 py-2.5 text-xs font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap"
                >
                  <i className={busy ? 'ri-loader-4-line animate-spin' : 'ri-add-line'}></i>
                  {busy ? 'Création…' : 'Créer le formulaire lié'}
                </button>
              </div>
            )}

            {error && (
              <p className="mt-3 flex items-start gap-1.5 text-xs text-red-600">
                <i className="ri-error-warning-line mt-0.5"></i>
                {error}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}